import type { ReactElement } from 'react'
import { kebabCase } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import Immutable from 'immutable'
import cc from 'classcat'

import { getNormalizedHeaderType } from '../../utils/themes'
import { getPlain } from '../store/utils'
import { setHasThemeLoaded } from '../store/actions'
import { useTranslate } from '../utils/translate'
import Notification from './templateComponents/Notification'
import ThemeView from './ThemeView'

type ThemeProps = {
  currentView: string
  withLayout?: boolean
  children?: (renderView: (view: string, props: any) => ReactElement, themeComponentProps: any) => ReactElement
  error?: any
}

export default function Theme({ currentView, withLayout = false, error, children }: Readonly<ThemeProps>) {
  const t = useTranslate('shop')

  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const isUnsupportedBrowser = useSelector<State, boolean>((state) => state.getIn(['view', 'isUnsupportedBrowser']))
  const hasThemeLoaded = useSelector<State, boolean>((state) => state.getIn(['view', 'hasThemeLoaded']))
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))
  const defaultThemeSettings = useSelector<State, Theme.Settings>((state) =>
    state.get('defaultThemeSettings', Immutable.Map()).toJS(),
  )
  const isEditorMode = useSelector<State, boolean>((state) => state.getIn(['view', 'editorMode']))
  const isEcommerceDisabled = useSelector<State, boolean>((state) =>
    Boolean(
      state
        .getIn(['shop', 'attributes'])
        .find(
          (attribute: ImmutableMap) =>
            attribute.get('name') === 'ecommerce:disabled' && attribute.get('value') === 'true',
        ),
    ),
  )
  const taxationByDestinationCountry = useSelector<State, boolean>((state) =>
    state.getIn(['tax', 'taxationByDestinationCountry']),
  )
  const navigation = useSelector<State, ImmutableMap>((state) => state.get('navigation'))
  const breadcrumb = useSelector<State, ImmutableList>((state) => state.get('breadcrumb'))
  const legalPages = useSelector<State, ImmutableList>((state) => state.get('legalPages'))
  const footerPages = useSelector<State, ImmutableList>((state) => state.get('footerPages'))
  const scriptTags = useSelector<State, ImmutableList>((state) => state.get('scriptTags'))

  const dispatch = useDispatch()

  const previewTheme = useSearchParams()[0]?.get('previewTheme')

  // might be undefined during language switch, some template components (e.g Cart)
  // will break hard when we render during that transition. Bare minimum template render props.
  // However we must make sure to only not render the respective template but load the stylesheet (see below)
  // The views passing their own props take care of checking these themselves
  const preventRendering = !error && (!shop || !navigation || !breadcrumb || !legalPages)

  // Use `shop.get('isInEU')` as fallback if the shop attribute can't be found
  const isInEU =
    getPlain(shop.get('attributes', [])).find((attribute) => attribute.name === 'isInEU')?.value ?? shop.get('isInEU')

  const themeSettings = shop.get('themeSettings').toJS()
  const themeComponentProps = {
    shop: shop?.toJS() ?? {},
    scriptTags: scriptTags?.toJS() ?? [],
    navigation: navigation?.get('storefront').toJS() ?? [],
    breadcrumb: breadcrumb?.toJS() ?? [],
    footerPages: footerPages?.toJS() ?? [],
    legalPages:
      legalPages?.toJS().reduce((items, item) => {
        if (item.isVisible) {
          // In non-Beyond shops, `title` is already translated
          if (isBeyondShop) item.title = t(item.title)

          items.push(item)
        }

        return items
      }, []) ?? [],
    isTaxationByDestinationCountryEnabled: !isBeyondShop && taxationByDestinationCountry && isInEU,
    isPreview: Boolean(previewTheme),
    isEditor: isEditorMode,
  }

  // cast error coming from react-router loader
  const plainError = getPlain(error)
  if (plainError?.status) Object.assign(plainError, { statusCode: plainError.status })

  const content = plainError
    ? renderView('Error', { shop: shop.toJS(), error: plainError })
    : children?.(renderView, themeComponentProps)

  const viewTemplate = withLayout
    ? renderView('Layout', {
        ...themeComponentProps,
        content,
        currentView,
      })
    : content

  const editorStorefrontLang = isEditorMode ? shop.get('locale').substring(0, 2) : null
  const headerType = getNormalizedHeaderType(
    themeSettings?.themeHeader?.headerType || defaultThemeSettings?.themeHeader?.headerType,
  )

  // A loading state is only needed upon theme switch on the client.
  // When hasThemeLoaded is `undefined`, it means that the component is rendered on the server,
  // or just mounted. In these cases, the theme is immediately ready.
  const isLoading = hasThemeLoaded === undefined ? false : !hasThemeLoaded

  // This effect is needed to let the editor bottom bar appear/disappear correctly when
  // switching from the main editor view to the theme switch view and vice versa.
  // It is only relevant in the editor.
  useEffect(() => {
    if (isEditorMode && hasThemeLoaded === undefined) {
      dispatch(setHasThemeLoaded(true))
    }
    return () => {
      if (isEditorMode && hasThemeLoaded && window.location.pathname.endsWith('/editor/themes')) {
        dispatch(setHasThemeLoaded(undefined))
      }
    }
  }, [hasThemeLoaded, isEditorMode, dispatch])

  return (
    <div
      className={cc([
        'body',
        {
          'no-ecommerce': isEcommerceDisabled,
          [`header-type-${kebabCase(headerType)}`]: headerType,
        },
      ])}
      lang={editorStorefrontLang}
    >
      {isUnsupportedBrowser && (
        <div className="unsupported-browser">
          <div className="unsupported-browser-info">
            {t('components.unsupportedBrowserNotice.explanation')}{' '}
            <a
              className="unsupported-browser-info-link"
              href="https://browser-update.org/update-browser.html"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              {t('components.unsupportedBrowserNotice.link.label')}
            </a>
          </div>
        </div>
      )}
      {isLoading ? <div className="editor-content-spinner"></div> : !preventRendering && viewTemplate}
      <Notification />
    </div>
  )
}

function renderView(view: string, props: any) {
  return <ThemeView view={view} {...props} />
}
